import { ACTIVE_LOCALES, LOCALE_META } from "@iec-id/shared/constants";
import { localeCompare, localeEndonym } from "@iec-id/shared/i18n";
import type { SearchSelectOption } from "@iec-id/ui";

/**
 * Списки для полей мастера. Ни одна подпись здесь не хранится строкой:
 * названия языков и поясов даёт `Intl` по текущей локали — иначе добавление
 * четырнадцатой локали означало бы четырнадцать словарей.
 */

/** Часовой пояс по умолчанию, если браузер молчит (совпадает с дефолтом БД). */
const FALLBACK_TIMEZONE = "Europe/Prague";

const timezoneCache = new Map<string, SearchSelectOption[]>();
let languageCache: SearchSelectOption[] | undefined;

/**
 * Языки интерфейса — активные локали, каждая подписана на себе самой
 * («Русский», «English»). Пикер языка обязан быть списком, а не тумблером
 * на два значения (rules/i18n.md §3.1).
 */
export function languageOptions(): SearchSelectOption[] {
	if (languageCache) return languageCache;

	// Ищут язык теми же четырьмя написаниями, что и в переключателе шапки:
	// эндоним на экране, а в поиске ещё код и английское имя — «gr» не совпадает
	// с «Ελληνικά», и без этого найти язык может только тот, кто на нём пишет.
	languageCache = ACTIVE_LOCALES.map(
		(code) =>
			({
				value: code,
				label: localeEndonym(code),
				keywords: LOCALE_META[code].englishName,
			}) satisfies SearchSelectOption,
	);
	return languageCache;
}

/** Пояс браузера. Именно он подставляется в шаг 3 по умолчанию. */
export function detectTimezone(): string {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone || FALLBACK_TIMEZONE;
	} catch {
		return FALLBACK_TIMEZONE;
	}
}

/** «GMT+2» → «UTC+2»; ровно та подпись, что в макете 4b-3. */
function utcOffset(timeZone: string): string {
	try {
		const parts = new Intl.DateTimeFormat("en-US", {
			timeZone,
			timeZoneName: "shortOffset",
		}).formatToParts(new Date());
		const raw = parts.find((part) => part.type === "timeZoneName")?.value ?? "GMT";
		return raw.replace("GMT", "UTC");
	} catch {
		return "UTC";
	}
}

/**
 * Все зоны IANA, которые знает движок, — человек может жить где угодно, а
 * короткий список пришлось бы чинить при каждой новой стране обслуживания.
 * Список считается один раз на локаль: 400 форматтеров на каждый рендер — дорого.
 */
export function timezoneOptions(locale: string): SearchSelectOption[] {
	const cached = timezoneCache.get(locale);
	if (cached) return cached;

	const zones = new Set<string>(Intl.supportedValuesOf("timeZone"));
	zones.add(detectTimezone());

	const compare = localeCompare(locale);
	const options = [...zones].toSorted(compare).map(
		(zone) =>
			({
				value: zone,
				label: `${zone.replaceAll("_", " ")} (${utcOffset(zone)})`,
			}) satisfies SearchSelectOption,
	);

	timezoneCache.set(locale, options);
	return options;
}
